import TopHeader from "../../components/header/top-header/TopHeader";
import styles from "./Header.module.scss";
import SearchBar from "../../components/header/searchbar/SearchBar";
import UserIcon from "../../assets/svgs/header/UserIcon";
import CartIcon from "../../assets/svgs/header/CartIcon";
import HeartIcon from "../../assets/svgs/header/HeartIcon";
import { useBasketWishlist } from "../../context/BasketWishlistContext";
import { useAuth } from "../../context/AuthContext";
import ArrowDown from "../../assets/svgs/header/ArrowDown";
import { useEffect, useRef, useState } from "react";
import UserIcon2 from "../../assets/svgs/header/UserIcon2";
import Notification from "../../assets/svgs/header/Notification";
import LogoutIcon from "../../assets/svgs/header/LogoutIcon";
import { eraseCookie } from "../../utils/cookies";
import AuthService from "../../api/services/auth.service";
import LocationIcon from "../../assets/svgs/header/LocationIcon";
import LocalizedLink from "../../components/common/localized-link/LocalizedLink";
import { useTranslation } from "react-i18next";
import OrdersIcon from "../../assets/svgs/header/OrdersIcon";
import BurgerIcon from "../../assets/svgs/header/BurgerIcon";
import ProductsService from "../../api/services/products.service";
import { useLanguage } from "../../context/LanguageContext";

interface CategoryChild {
  children: CategoryChild[];
  id: number;
  image: string;
  name: string;
  name_az?: string;
  name_en?: string;
  name_es?: string;
  name_de?: string;
  parent_id: number | null;
}

interface CategoriesTypes {
  children: CategoryChild[];
  id: number;
  image: string;
  name: string;
  name_az?: string;
  name_en?: string;
  name_es?: string;
  name_de?: string;
  parent_id: number | null;
}

const Header = () => {
  const { basketCount, wishlistCount } = useBasketWishlist();
  const { isLoggedIn } = useAuth();
  const [dropdown, setDropdown] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navDropdownRef = useRef<HTMLDivElement>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const { t } = useTranslation("translation");
  const { currentLanguage } = useLanguage();

  // Categories state
  const [categories, setCategories] = useState<CategoriesTypes[]>([]);
  const [categoriesMenuOpen, setCategoriesMenuOpen] = useState(false);
  const categoriesRef = useRef<HTMLDivElement>(null);
  const categoriesDropdownRef = useRef<HTMLDivElement>(null);
  const categoriesLeaveRef = useRef<NodeJS.Timeout | null>(null);

  const leaveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const getData = async () => {
      try {
        const res = await ProductsService.getCategories();
        setCategories(res.data);
      } catch (error) {
        console.error("Error fetching categories:", error);
      }
    };
    getData();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setDropdown(false);
      }
      if (
        navDropdownRef.current &&
        !navDropdownRef.current.contains(event.target as Node)
      ) {
        setActiveDropdown(null);
      }
      if (
        categoriesRef.current &&
        !categoriesRef.current.contains(event.target as Node) &&
        categoriesDropdownRef.current &&
        !categoriesDropdownRef.current.contains(event.target as Node)
      ) {
        setCategoriesMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      if (leaveTimeoutRef.current) {
        clearTimeout(leaveTimeoutRef.current);
        leaveTimeoutRef.current = null;
      }
    };
  }, []);

  const handleLogout = async () => {
    try {
      eraseCookie("LuxMartClientAccessToken");
      AuthService.logout();
      window.location.reload();
    } catch (error: any) {}
  };

  const getLocalizedCategoryName = (
    category: CategoriesTypes | CategoryChild,
  ) => {
    if (!category) return "";

    switch (currentLanguage) {
      case "az":
        return category.name_az || category.name;
      case "en":
        return category.name_en || category.name;
      case "es":
        return category.name_es || category.name;
      case "de":
        return category.name_de || category.name;
      default:
        return category.name;
    }
  };

  const handleCategoriesEnter = () => {
    if (categoriesLeaveRef.current) {
      clearTimeout(categoriesLeaveRef.current);
      categoriesLeaveRef.current = null;
    }
    setCategoriesMenuOpen(true);
  };

  const handleCategoriesLeave = () => {
    if (categoriesLeaveRef.current) {
      clearTimeout(categoriesLeaveRef.current);
      categoriesLeaveRef.current = null;
    }
    categoriesLeaveRef.current = setTimeout(() => {
      setCategoriesMenuOpen(false);
    }, 150);
  };

  const handleDropdownHover = (dropdownName: string) => {
    if (leaveTimeoutRef.current) {
      clearTimeout(leaveTimeoutRef.current);
      leaveTimeoutRef.current = null;
    }
    setActiveDropdown(dropdownName);
  };

  const handleDropdownLeave = () => {
    if (leaveTimeoutRef.current) {
      clearTimeout(leaveTimeoutRef.current);
      leaveTimeoutRef.current = null;
    }

    const timeout = setTimeout(() => {
      setActiveDropdown(null);
    }, 150);

    leaveTimeoutRef.current = timeout;
  };

  const handleDropdownEnter = () => {
    if (leaveTimeoutRef.current) {
      clearTimeout(leaveTimeoutRef.current);
      leaveTimeoutRef.current = null;
    }
  };

  const partnerDropdown = [
    { to: "/becomeseller", label: t("become_seller") },
    { to: "/partnership", label: t("partnership") },
  ];

  return (
    <>
      <div className={styles.header}>
        <TopHeader />
        <div className={styles.main_header}>
          <div className="container">
            <div className={styles.content}>
              <div className={styles.logo}>
                <div
                  className={styles.menu}
                  onClick={() => setMenuOpen(!menuOpen)}
                >
                  <BurgerIcon />
                </div>
                <LocalizedLink to="/">
                  <h1 className={styles.name}>LuxMart</h1>
                </LocalizedLink>
              </div>

              <div className={styles.search_section}>
                <div
                  className={styles.categories_button_wrapper}
                  ref={categoriesRef}
                  onMouseEnter={handleCategoriesEnter}
                  onMouseLeave={handleCategoriesLeave}
                >
                  <button className={styles.categories_button}>
                    <span className={styles.grid_icon}>
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                      >
                        <rect
                          x="3"
                          y="3"
                          width="7"
                          height="7"
                          rx="1"
                          fill="currentColor"
                        />
                        <rect
                          x="14"
                          y="3"
                          width="7"
                          height="7"
                          rx="1"
                          fill="currentColor"
                        />
                        <rect
                          x="3"
                          y="14"
                          width="7"
                          height="7"
                          rx="1"
                          fill="currentColor"
                        />
                        <rect
                          x="14"
                          y="14"
                          width="7"
                          height="7"
                          rx="1"
                          fill="currentColor"
                        />
                      </svg>
                    </span>
                    <span>{t("categories") || "Kateqoriyalar"}</span>
                  </button>
                </div>
                <SearchBar />
              </div>
              <div className={styles.actions_wrapper}>
                <div
                  className={`${styles.blur_background} ${
                    menuOpen ? styles.open : ""
                  }`}
                  onClick={() => setMenuOpen(false)}
                ></div>
                <div className={styles.mob_logo}>
                  <div
                    className={styles.menu}
                    onClick={() => setMenuOpen(!menuOpen)}
                  >
                    <BurgerIcon />
                  </div>
                  <LocalizedLink to="/">
                    <h1 className={styles.name}>LuxMart</h1>
                  </LocalizedLink>
                </div>
                <div className={styles.actions}>
                  {isLoggedIn ? (
                    <div ref={dropdownRef} className={styles.signed_in}>
                      <div
                        className={styles.user_toggle}
                        onClick={() => setDropdown((prev) => !prev)}
                      >
                        <UserIcon />
                        <ArrowDown />
                      </div>

                      {dropdown && (
                        <ul
                          className={styles.dropdown}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <LocalizedLink
                            to="/auth/info"
                            className={styles.list}
                            onClick={() => setDropdown(false)}
                          >
                            <UserIcon2 />
                            <p>{t("my_account")}</p>
                          </LocalizedLink>
                          <LocalizedLink
                            to="/auth/notifications"
                            className={styles.list}
                            onClick={() => setDropdown(false)}
                          >
                            <Notification />
                            <p>{t("my_notification")}</p>
                          </LocalizedLink>
                          <LocalizedLink
                            to="/auth/addresses"
                            className={styles.list}
                            onClick={() => setDropdown(false)}
                          >
                            <LocationIcon />
                            <p>{t("addresses")}</p>
                          </LocalizedLink>
                          <LocalizedLink
                            to="/orders"
                            className={styles.list}
                            onClick={() => setDropdown(false)}
                          >
                            <OrdersIcon />
                            <p>{t("orders")}</p>
                          </LocalizedLink>
                          <button
                            className={styles.log_out}
                            onClick={handleLogout}
                          >
                            <LogoutIcon />
                            <p>{t("log_out")}</p>
                          </button>
                        </ul>
                      )}
                    </div>
                  ) : (
                    <LocalizedLink to="/auth/login" className={styles.sign_in}>
                      {t("login")}
                      <UserIcon />
                    </LocalizedLink>
                  )}
                  <LocalizedLink to="/wishlist" className={styles.favorite}>
                    <HeartIcon />
                    {wishlistCount > 0 && (
                      <div className={styles.count}>{wishlistCount}</div>
                    )}
                  </LocalizedLink>
                  <LocalizedLink to="/basket" className={styles.cart_icon}>
                    <CartIcon />
                    {basketCount > 0 && (
                      <div className={styles.count}>{basketCount}</div>
                    )}
                  </LocalizedLink>
                </div>
              </div>
            </div>
          </div>
          {categoriesMenuOpen && (
            <div
              ref={categoriesDropdownRef}
              className={styles.categories_dropdown}
              onMouseEnter={handleCategoriesEnter}
              onMouseLeave={handleCategoriesLeave}
            >
              <div className="container">
                <div className={styles.categories_dropdown_content}>
                  <div className={styles.categories_grid}>
                    {categories?.map((cat) => (
                      <div key={cat.id} className={styles.category_column}>
                        <div className={styles.category_header}>
                          {cat.image && (
                            <img
                              src={cat.image}
                              alt={getLocalizedCategoryName(cat)}
                            />
                          )}
                          <LocalizedLink
                            to={`/products/${cat.id}`}
                            className={styles.category_title}
                            onClick={() => setCategoriesMenuOpen(false)}
                          >
                            {getLocalizedCategoryName(cat)}
                          </LocalizedLink>
                        </div>
                        {cat.children && cat.children.length > 0 && (
                          <div className={styles.subcategory_list}>
                            {cat.children.map((subCat) => (
                              <LocalizedLink
                                key={subCat.id}
                                to={`/products/${subCat.id}`}
                                className={styles.subcategory_item}
                                onClick={() => setCategoriesMenuOpen(false)}
                              >
                                {getLocalizedCategoryName(subCat)}
                              </LocalizedLink>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
        <div className={styles.navlinks_section}>
          <div className="container">
            <div className={styles.navlinks}>
              <LocalizedLink to="/" className={styles.nav_link}>
                {t("home")}
              </LocalizedLink>
              <LocalizedLink to="/stores" className={styles.nav_link}>
                {t("stores")}
              </LocalizedLink>
              <LocalizedLink to="/about" className={styles.nav_link}>
                {t("about_title")}
              </LocalizedLink>

              {/* Partnership Dropdown */}
              <div
                className={styles.nav_dropdown}
                ref={navDropdownRef}
                onMouseEnter={() => handleDropdownHover("partnership")}
                onMouseLeave={handleDropdownLeave}
              >
                <button className={styles.nav_link}>
                  {t("partnership")}
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 12 12"
                    fill="currentColor"
                    style={{ marginLeft: "4px" }}
                  >
                    <path d="M6 8L2 4h8l-4 4z" />
                  </svg>
                </button>
                {activeDropdown === "partnership" && (
                  <div
                    className={styles.dropdown_menu}
                    onMouseEnter={handleDropdownEnter}
                  >
                    {partnerDropdown.map((item, idx) => (
                      <LocalizedLink
                        key={idx}
                        to={item.to}
                        className={styles.dropdown_item}
                      >
                        {item.label}
                      </LocalizedLink>
                    ))}
                  </div>
                )}
              </div>

              <LocalizedLink to="/privacy-policy" className={styles.nav_link}>
                {t("privacy_policy")}
              </LocalizedLink>
              <LocalizedLink to="/return-delivery" className={styles.nav_link}>
                {t("return_delivery")}
              </LocalizedLink>
              <LocalizedLink to="/contact" className={styles.nav_link}>
                {t("contact")}
              </LocalizedLink>
            </div>
          </div>
        </div>
      </div>
      <div className={`${styles.sideMenu} ${menuOpen ? styles.open : ""}`}>
        <div className={styles.head}>
          <LocalizedLink
            to="/"
            className={styles.sideMenu_logo}
            onClick={() => setMenuOpen(false)}
          >
            <h2>LuxMart</h2>
          </LocalizedLink>
          <button
            className={styles.closeBtn}
            onClick={() => setMenuOpen(false)}
            aria-label="Close menu"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
        <div className={styles.mob_links}>
          <LocalizedLink
            to="/"
            className={styles.link}
            onClick={() => setMenuOpen(false)}
          >
            <span className={styles.link_icon}>
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
                <polyline points="9 22 9 12 15 12 15 22" />
              </svg>
            </span>
            <span>{t("home")}</span>
            <svg
              className={styles.link_arrow}
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            >
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </LocalizedLink>
          <LocalizedLink
            to="/stores"
            className={styles.link}
            onClick={() => setMenuOpen(false)}
          >
            <span className={styles.link_icon}>
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M3 9l1-6h16l1 6" />
                <path d="M3 9a2 2 0 002 2 2 2 0 002-2 2 2 0 002 2 2 2 0 002-2 2 2 0 002 2 2 2 0 002-2" />
                <path d="M5 11v9h14v-9" />
              </svg>
            </span>
            <span>{t("stores")}</span>
            <svg
              className={styles.link_arrow}
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            >
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </LocalizedLink>
          <LocalizedLink
            to="/about"
            className={styles.link}
            onClick={() => setMenuOpen(false)}
          >
            <span className={styles.link_icon}>
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
            </span>
            <span>{t("about_title")}</span>
            <svg
              className={styles.link_arrow}
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            >
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </LocalizedLink>
          <LocalizedLink
            to="/becomeseller"
            className={styles.link}
            onClick={() => setMenuOpen(false)}
          >
            <span className={styles.link_icon}>
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <rect x="2" y="7" width="20" height="14" rx="2" />
                <path d="M16 7V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v2" />
              </svg>
            </span>
            <span>{t("become_seller")}</span>
            <svg
              className={styles.link_arrow}
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            >
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </LocalizedLink>
          <LocalizedLink
            to="/partnership"
            className={styles.link}
            onClick={() => setMenuOpen(false)}
          >
            <span className={styles.link_icon}>
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <path d="M23 21v-2a4 4 0 00-3-3.87" />
                <path d="M16 3.13a4 4 0 010 7.75" />
              </svg>
            </span>
            <span>{t("partnership")}</span>
            <svg
              className={styles.link_arrow}
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            >
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </LocalizedLink>
          <LocalizedLink
            to="/contact"
            className={styles.link}
            onClick={() => setMenuOpen(false)}
          >
            <span className={styles.link_icon}>
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.8a19.79 19.79 0 01-3.07-8.67A2 2 0 012 1h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.09 8.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z" />
              </svg>
            </span>
            <span>{t("contact")}</span>
            <svg
              className={styles.link_arrow}
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            >
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </LocalizedLink>
          <LocalizedLink
            to="/return-delivery"
            className={styles.link}
            onClick={() => setMenuOpen(false)}
          >
            <span className={styles.link_icon}>
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M1 3h15v13H1z" />
                <path d="M16 8h4l3 3v5h-7V8z" />
                <circle cx="5.5" cy="18.5" r="2.5" />
                <circle cx="18.5" cy="18.5" r="2.5" />
              </svg>
            </span>
            <span>{t("return_delivery")}</span>
            <svg
              className={styles.link_arrow}
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            >
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </LocalizedLink>
          <LocalizedLink
            to="/privacy-policy"
            className={styles.link}
            onClick={() => setMenuOpen(false)}
          >
            <span className={styles.link_icon}>
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
              </svg>
            </span>
            <span>{t("privacy_policy")}</span>
            <svg
              className={styles.link_arrow}
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            >
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </LocalizedLink>
        </div>
      </div>
    </>
  );
};

export default Header;
